<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useVoucherSSBTStore } from '@/stores/voucher/voucherSSBTStore'
import { ButtonVariant, SCButton, SCInput, SCModalNext } from '@nsftx/seven-components'
import {
  useVoucherStore,
  voucherErrorParser,
  vouchersApiService
} from '@nsftx/seven-client-retail-vue-sgp'
import { useDeviceManagementStore } from '@nsftx/seven-client-retail-vue-dm'
import { BaseError, logService } from '@nsftx/seven-client-retail-utils'
import { printVoucher } from '@/modules/voucher/voucherSSBTService'
import { useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import i18n from '@/plugins/translationsPlugin'
import { getWallets } from '@/modules/wallet/walletService'
import errorParser from '@/utils/errorParser'

const LOG_PREFIX = '[VoucherPin]'

const { t } = i18n.global

const voucherPinInput = ref<string>('')

const voucherSSBTStore = useVoucherSSBTStore()
const { isVoucherPinOpened } = storeToRefs(voucherSSBTStore)
const { closeVoucherPinModal } = voucherSSBTStore
const { showNotification } = useNotificationsStore()

const { appData } = storeToRefs(useDeviceManagementStore())

const voucherStore = useVoucherStore()
const { voucher } = storeToRefs(voucherStore)

const activateVoucher = async () => {
  if (!appData.value || !voucher.value || !voucherPinInput.value) {
    throw new BaseError('Missing required voucher activation data', 'ST_VOUCHER_ACTIVATION_ERROR')
  }

  try {
    await vouchersApiService.redeemVoucher(
      appData.value.betshop.companyUuid,
      voucher.value.id,
      voucherPinInput.value
    )

    logService.info(`${LOG_PREFIX} Voucher redeemed successfully`, {
      code: 'ST_VOUCHER_REDEEM_SUCCESS'
    })

    getWallets('ui') // update wallet balance after voucher activation
      .catch((err) => {
        logService.error(
          `${LOG_PREFIX} Failed to get wallets for the punter after voucher activation.`,
          {
            code: 'ST_GET_WALLETS_FAILED',
            ...errorParser.parseUpstream(err)
          }
        )
      })

    await printVoucher(voucher.value!, 'payout')

    closeVoucherPinModal()
  } catch (err) {
    logService.error(`${LOG_PREFIX} Failed to redeem voucher`, {
      ...voucherErrorParser.parseUpstream(err),
      code: 'ST_VOUCHER_REDEEM_ERROR'
    })
    showNotification({
      variant: 'error',
      text: [t('voucher_redeem_failed')]
    })
  }
}

const onClose = () => {
  voucherPinInput.value = ''
  voucherStore.clearVoucherData()
}
</script>
<template>
  <SCModalNext
    v-model="isVoucherPinOpened"
    width="400px"
    with-close-icon
    class="st-voucher-pin-modal"
    @close="onClose"
  >
    <template #content>
      <div class="st-p-6 st-w-100">
        <div class="sc-text-caption-1 sc-text-primary-2 sc-mb-6">
          {{ $t('voucher_pin_description') }}
        </div>
        <SCInput
          v-model="voucherPinInput"
          autocomplete="off"
          :placeholder="$t('voucher_pin_placeholder')"
          block
        />
      </div>
      <div class="st-d-flex st-ml-auto sc-pt-6">
        <SCButton :variant="ButtonVariant.Primary" @click="activateVoucher">
          {{ $t('voucher_activate') }}
        </SCButton>
        <SCButton :variant="ButtonVariant.Primary" @click="closeVoucherPinModal">
          {{ $t('common_close') }}
        </SCButton>
      </div>
    </template>
  </SCModalNext>
</template>

<style scoped></style>
